import React, { useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';

interface FormattedMessageProps {
  text: string;
  className?: string;
  isOutgoing?: boolean;
}

/**
 * Компонент для отображения форматированного текста сообщения
 * Поддерживает HTML форматирование (Telegram HTML format) и эмодзи
 */
const FormattedMessage: React.FC<FormattedMessageProps> = ({ text, className = '', isOutgoing = false }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  if (!text) return null;

  // Определяем стили в зависимости от направления сообщения
  const codeBgClass = isOutgoing ? 'bg-white bg-opacity-20' : 'bg-gray-200';
  const linkClass = isOutgoing ? 'text-blue-100 underline' : 'text-blue-600 underline';
  const borderClass = isOutgoing ? 'border-white border-opacity-30' : 'border-gray-400 border-opacity-30';
  const spoilerClass = isOutgoing ? 'bg-white bg-opacity-30' : 'bg-gray-300';

  // Обрабатываем HTML и добавляем стили для Telegram-специфичных элементов
  let processedHtml = text;
  
  // Заменяем переносы строк на <br>
  processedHtml = processedHtml.replace(/\n/g, '<br>');
  
  // Добавляем стили для <code> (моношрифт)
  processedHtml = processedHtml.replace(/<code>/g, `<code class="font-mono text-xs ${codeBgClass} px-1 py-0.5 rounded">`);
  
  // Добавляем стили для <pre> (блок кода)
  processedHtml = processedHtml.replace(/<pre>/g, `<pre class="block ${codeBgClass} p-2 rounded text-xs font-mono whitespace-pre overflow-x-auto my-1">`);
  
  // Добавляем стили для <a> (ссылки)
  processedHtml = processedHtml.replace(/<a\s+href="([^"]*)">/g, `<a href="$1" target="_blank" rel="noopener noreferrer" class="${linkClass}">`);
  
  // Добавляем стили для <blockquote> (цитаты)
  processedHtml = processedHtml.replace(/<blockquote>/g, `<blockquote class="border-l-2 ${borderClass} pl-2 my-1 italic">`);
  
  // Обрабатываем спойлеры (tg-spoiler) - добавляем класс для CSS стилизации
  processedHtml = processedHtml.replace(/<span class="tg-spoiler">/g, `<span class="tg-spoiler ${spoilerClass} rounded px-1 cursor-pointer" style="filter: blur(4px); transition: filter 0.2s;">`);

  // Разрешаем только безопасные HTML теги, которые поддерживает Telegram
  const sanitizedHtml = DOMPurify.sanitize(processedHtml, {
    ALLOWED_TAGS: ['b', 'strong', 'i', 'em', 'u', 'ins', 's', 'strike', 'del', 'code', 'pre', 'a', 'blockquote', 'span', 'br'],
    ALLOWED_ATTR: ['href', 'class', 'style'],
    ALLOW_DATA_ATTR: false
  });

  // Устанавливаем обработчики для спойлеров после рендеринга
  useEffect(() => {
    if (!containerRef.current) return;

    const spoilers = containerRef.current.querySelectorAll('.tg-spoiler');
    const cleanupFunctions: Array<() => void> = [];

    spoilers.forEach((spoiler) => {
      const element = spoiler as HTMLElement;
      // Устанавливаем начальное размытие
      element.style.filter = 'blur(4px)';
      element.style.transition = 'filter 0.2s';
      
      // Обработчики для каждого спойлера отдельно
      const handleMouseEnter = () => {
        element.style.filter = 'blur(0px)';
      };
      const handleMouseLeave = () => {
        element.style.filter = 'blur(4px)';
      };
      
      element.addEventListener('mouseenter', handleMouseEnter);
      element.addEventListener('mouseleave', handleMouseLeave);
      
      cleanupFunctions.push(() => {
        element.removeEventListener('mouseenter', handleMouseEnter);
        element.removeEventListener('mouseleave', handleMouseLeave);
      });
    });

    return () => {
      cleanupFunctions.forEach(cleanup => cleanup());
    };
  }, [sanitizedHtml]);

  return (
    <div 
      ref={containerRef}
      className={`text-sm whitespace-pre-wrap break-words ${className}`}
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
    />
  );
};

export default FormattedMessage;

